'use client';

import Loader from '@/components/ui/Loader';
import { useFestivalContext } from '@/contexts/FestivalContext';
import { Link } from '@/i18n/navigation';
import { useUserStore } from '@/store/useUserStore';
import { Translatable } from '@/types/carousels';
import { getLocalizedValue } from '@/utils/locale';
import { createClient } from '@/utils/supabase/client';
import { cn } from '@/utils/tailwind-merge';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import BenefitRowItem from '../components/BenefitRowItem';
import StreamingAccessCard from '../components/StreamingAccessCard';
import TicketRowItem, { GetUserTicketsResponse } from '../components/TicketRowItem';
import TicketRowItemMobile from '../components/TicketRowItemMobile';
import TicketsTableSortingBar from '../components/TicketsTableSortingBar';

interface MyTicketsPageData {
    id: string;
    festival_id: string | null;
    tickets_description: Translatable | null;
}

export default function MyTickets({ params: { locale } }: { params: { locale: string } }) {
    const t = useTranslations();
    const supabase = createClient();
    const { slug: festivalSlug, festival } = useFestivalContext();
    const [ticketsData, setTicketsData] = useState<GetUserTicketsResponse | null>(null);
    const [pageData, setPageData] = useState<MyTicketsPageData | null>(null);
    const { user } = useUserStore();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user && festival?.id) {
            let isMounted = true;

            const fetchData = async () => {
                setLoading(true);

                try {
                    const [ticketsResult, pageDataResult] = await Promise.all([
                        supabase
                            .rpc('get_user_tickets_v3', {
                                user_email_param: user.email,
                                locale,
                                festival_id_param: festival.id,
                            })
                            .returns<GetUserTicketsResponse>(),
                        supabase
                            .from('account')
                            .select('*')
                            .eq('festival_id', festival.id)
                            .returns<MyTicketsPageData[]>()
                            .single(),
                    ]);

                    if (!isMounted) return;

                    if (ticketsResult.error) {
                        console.error('[MyTickets] Error fetching tickets:', ticketsResult.error);
                    } else {
                        const tickets = ticketsResult.data?.tickets || [];
                        const benefits = ticketsResult.data?.benefits || [];

                        // Log diagnostic information for ticket assignment issues
                        if (tickets.length === 0 && benefits.length === 0) {
                            console.warn(
                                '[MyTickets] No tickets found for user:',
                                user.email,
                                'festival:',
                                festival.id,
                            );
                        } else {
                            console.log(
                                `[MyTickets] Found ${tickets.length} tickets and ${benefits.length} benefits for user:`,
                                user.email,
                            );
                        }

                        setTicketsData(ticketsResult.data);
                    }

                    if (pageDataResult.error) {
                        console.error('Error fetching page data:', pageDataResult.error);
                    } else {
                        setPageData(pageDataResult.data);
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    if (isMounted) {
                        setLoading(false);
                    }
                }
            };

            fetchData();

            return () => {
                isMounted = false;
            };
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, festival?.id, locale]);

    return (
        <div className="flex flex-col gap-12 mt-12 max-xl:w-[calc(100%+20px)]">
            <div className="flex flex-col gap-3 lg:gap-7">
                <h4 className="text-white text-[32px] lg:text-[46px] uppercase leading-none">
                    {t('Common.tickets')}
                </h4>
                <p
                    className="font-helvetica-neue text-lg font-light normal-case"
                    dangerouslySetInnerHTML={{
                        __html: getLocalizedValue(
                            pageData?.tickets_description,
                            locale as 'en' | 'fr',
                        ),
                    }}
                />
                {loading ? (
                    <Loader />
                ) : (
                    <div className="flex flex-col ">
                        {ticketsData?.tickets?.length ? (
                            <>
                                <div className="hidden lg:block">
                                    <TicketsTableSortingBar />
                                </div>

                                <div className="hidden lg:block">
                                    {ticketsData?.tickets?.map((t, i) => (
                                        <TicketRowItem {...t} key={t.ticketId + '-' + i} />
                                    ))}
                                </div>

                                <div className="lg:hidden">
                                    {ticketsData?.tickets?.map((t, i) => (
                                        <TicketRowItemMobile {...t} key={t.ticketId + '-' + i} />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="border border-white/50 w-full p-7 flex flex-col justify-center items-center max-xl:w-[calc(100%-40px)]">
                                <h5 className="uppercase text-h5 text-white/70 w-5/6 text-center">
                                    {t('Common.oops')}
                                </h5>
                                <h5 className="uppercase lg:text-h5 text-[22px] text-white/70 w-5/6 text-center">
                                    {t('Common.outOfTickets')}
                                </h5>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!!ticketsData?.gifts?.length && (
                <section className="flex flex-col gap-5">
                    <h4 className="text-white text-[32px] lg:text-[46px] uppercase leading-none">
                        {t('GiftPass.purchasedGifts')}
                    </h4>
                    <p className="font-helvetica-neue text-lg">{t('GiftPass.shareInstructions')}</p>
                    {ticketsData.gifts.map((gift) => (
                        <div
                            key={gift.ticketAllocationId}
                            className="border border-white/50 p-5 flex flex-col gap-3"
                        >
                            <h5 className="text-[24px] uppercase">{gift.name}</h5>
                            <p className="font-helvetica-neue">
                                {gift.isRedeemed
                                    ? t('GiftPass.redeemed')
                                    : gift.giftCode || t('GiftPass.preparing')}
                            </p>
                            {gift.giftCode && gift.targetTicketId && !gift.isRedeemed && (
                                <Link
                                    className="text-primary underline text-xl"
                                    href={`/tickets-and-venues/?redeem=gift&ticket=${encodeURIComponent(gift.targetTicketId)}`}
                                >
                                    {t('GiftPass.redemptionLink')}
                                </Link>
                            )}
                        </div>
                    ))}
                </section>
            )}

            <div className="flex flex-col gap-3 lg:gap-7">
                <h4 className="text-white text-[32px] lg:text-[46px] uppercase leading-none">
                    {t('Common.benefits')}
                </h4>
                {loading ? (
                    <Loader />
                ) : (
                    <div
                        className={cn(
                            'flex flex-col  border-white/50 max-xl:overflow-auto',
                            ticketsData?.benefits?.length && 'border-t',
                        )}
                    >
                        {ticketsData?.benefits?.length ? (
                            ticketsData?.benefits?.map((props, index) => (
                                <BenefitRowItem {...props} key={props.ticketId + '-' + index} />
                            ))
                        ) : (
                            <div className="border border-white/50 w-full p-7 flex flex-col justify-center items-center max-xl:w-[calc(100%-40px)]">
                                <h6 className="text-[22px] lg:text-h6 w-5/6 text-center uppercase text-white/70">
                                    {t('Common.noAvailableBenefits')}
                                </h6>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Streaming Access Section */}
            {ticketsData?.tickets && (
                <div className="flex flex-col gap-3 lg:gap-7">
                    <h4 className="text-white text-[32px] lg:text-[46px] uppercase leading-none">
                        {t('Common.streamingAccess')}
                    </h4>
                    {loading ? (
                        <Loader />
                    ) : (
                        <div
                            className={cn(
                                'flex flex-col border-white/50 max-xl:overflow-auto',
                                ticketsData?.tickets?.some((ticket) => ticket.voucher) &&
                                    'border-t',
                            )}
                        >
                            {ticketsData?.tickets?.filter((ticket) => ticket.voucher).length ? (
                                <div className="flex flex-col gap-4 py-4">
                                    {ticketsData.tickets
                                        .filter((ticket) => ticket.voucher)
                                        .map((ticket, index) => (
                                            <StreamingAccessCard
                                                key={ticket.ticketAllocationId + '-' + index}
                                                ticketAllocationId={ticket.ticketAllocationId}
                                                name={ticket.name}
                                                screening={ticket.screening || undefined}
                                                voucher={ticket.voucher}
                                            />
                                        ))}
                                </div>
                            ) : (
                                <div className="border border-white/50 w-full p-7 flex flex-col justify-center items-center max-xl:w-[calc(100%-40px)]">
                                    <h6 className="text-[22px] lg:text-h6 w-5/6 text-center uppercase text-white/70">
                                        {t('Common.noStreamingAccess')}
                                    </h6>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
